import React, { useEffect, useRef } from 'react';
import { usePusher } from '@hooks/usePusher';
import { useSession } from '@hooks/index';
import { api } from '@/trpc/react';
import { toast } from 'react-hot-toast';
import type { Channel } from 'pusher-js';
import { UpscaleCompletedPayload, AlbumProcessingStartedPayload } from '@lib/pusher/events';

/**
 * PUBLIC CHANNEL / EVENT CONSTANTS
 * Keep these in sync with your server-side emitter names.
 */
const PUBLIC_UPDATES_CHANNEL = 'theme-sample-updates';
const THEME_PROCESSED_EVENT = 'theme-processed';

/**
 * Component that lives near the root of the React tree (e.g. inside _app.tsx).
 * It registers all global Pusher event listeners exactly once so that every page
 * in the application – even full-screen pages that do **not** include the
 * standard <Layout/> component – benefit from real-time cache invalidation.
 */
export const PusherEventListener: React.FC = () => {
  const { client: pusherClient } = usePusher();
  const { session } = useSession();
  const apiUtils = api.useUtils();

  /* ------------------------------------------------------------------
   * Batched invalidation helpers – prevent hammering tRPC/react-query
   * when many Pusher events arrive in the same animation frame.
   * ------------------------------------------------------------------ */

  // Track albumIds that need a targeted invalidate
  const pendingAlbumInvalidationsRef = useRef<Set<string>>(new Set());
  // Simple flag so we only schedule one flush per tick
  const isFlushScheduledRef = useRef(false);

  const scheduleAlbumInvalidation = (albumId?: string) => {
    if (albumId) pendingAlbumInvalidationsRef.current.add(albumId);

    if (isFlushScheduledRef.current) return;

    isFlushScheduledRef.current = true;
    setTimeout(() => {
      // Invalidate specific albums first
      pendingAlbumInvalidationsRef.current.forEach(id => {
        apiUtils.albums.get.invalidate({ albumId: id });
      });

      // Also refresh list summaries once per batch
      apiUtils.albums.getAll.invalidate();

      // Reset for next batch
      pendingAlbumInvalidationsRef.current.clear();
      isFlushScheduledRef.current = false;
    }, 200); // 200 ms debounce window – tweak as needed
  };

  // Debounced full invalidate (used rarely – upscale etc.)
  const scheduleFullInvalidate = () => {
    if (isFlushScheduledRef.current) return; // piggy-back on existing flush if scheduled
    isFlushScheduledRef.current = true;
    setTimeout(() => {
      apiUtils.invalidate();
      pendingAlbumInvalidationsRef.current.clear();
      isFlushScheduledRef.current = false;
    }, 200);
  };

  /* ------------------------------------------------------------------
   * User-specific private channel listeners
   * ------------------------------------------------------------------ */

  useEffect(() => {
    if (!pusherClient || !session) return;

    // Ensure the user is signed-in on Pusher (required for user channel)
    if (!pusherClient.user.signin_requested) {
      pusherClient.signin();
    }

    const userChannel = pusherClient.user;

    const albumCompletedHandler = ({ albumId }: { albumId: string }) => {
      console.log('Pusher: album-completed received', { albumId });
      scheduleAlbumInvalidation(albumId);
    };

    const repoCloningCompletedHandler = () => {
      console.log('Pusher: repository-cloning-completed received');
      scheduleFullInvalidate();
    };

    const photoActionCompletedHandler = ({ albumId }: { albumId: string }) => {
      console.log('Pusher: photo-action-completed received', { albumId });
      scheduleAlbumInvalidation(albumId);
    };

    const upscaleCompletedHandler = (data: UpscaleCompletedPayload) => {
      console.log('Pusher: upscale-completed received', data);
      scheduleFullInvalidate();

      if (data.status !== 'completed') {
        toast.error(data.message || 'Image upscale failed. Please try again.');
      }
    };

    const albumProcessingStartedHandler = (data: AlbumProcessingStartedPayload) => {
      console.log('Pusher: album-processing-started received', data);
      scheduleAlbumInvalidation(data.albumId);
    };

    userChannel.bind('album-completed', albumCompletedHandler);
    userChannel.bind('repository-cloning-completed', repoCloningCompletedHandler);
    userChannel.bind('photo-action-completed', photoActionCompletedHandler);
    userChannel.bind('upscale-completed', upscaleCompletedHandler);
    userChannel.bind('album-processing-started', albumProcessingStartedHandler);

    return () => {
      userChannel.unbind('album-completed', albumCompletedHandler);
      userChannel.unbind('repository-cloning-completed', repoCloningCompletedHandler);
      userChannel.unbind('photo-action-completed', photoActionCompletedHandler);
      userChannel.unbind('upscale-completed', upscaleCompletedHandler);
      userChannel.unbind('album-processing-started', albumProcessingStartedHandler);
    };
  }, [pusherClient, session, apiUtils]);

  /* ------------------------------------------------------------------
   * Public channel listeners (no auth required)
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!pusherClient) return;

    let channelInstance: Channel | null = null;

    try {
      channelInstance = pusherClient.subscribe(PUBLIC_UPDATES_CHANNEL);
      console.log(`Pusher: Subscribed to public channel '${PUBLIC_UPDATES_CHANNEL}'`);
    } catch (error) {
      console.error('Pusher: Error subscribing to public channel:', error);
      return;
    }

    const themeProcessedHandler = (data: { themeId: string }) => {
      console.log('Pusher: theme-processed event received:', data);
      apiUtils.invalidate();
    };

    channelInstance.bind(THEME_PROCESSED_EVENT, themeProcessedHandler);
    console.log(`Pusher: Bound to event '${THEME_PROCESSED_EVENT}' on channel '${PUBLIC_UPDATES_CHANNEL}'`);

    return () => {
      if (channelInstance) {
        console.log(`Pusher: Unbinding '${THEME_PROCESSED_EVENT}' and unsubscribing from '${PUBLIC_UPDATES_CHANNEL}'`);
        channelInstance.unbind(THEME_PROCESSED_EVENT, themeProcessedHandler);
        pusherClient.unsubscribe(PUBLIC_UPDATES_CHANNEL);
      }
    };
  }, [pusherClient, apiUtils]);

  // This component does not render anything
  return null;
};
